import { useCallback, useEffect, useId, useRef } from 'react'
import type { ReactNode } from 'react'
import { createPortal } from 'react-dom'
import './Modal.css'

export interface ModalProps {
  open: boolean
  onClose: () => void
  title?: ReactNode
  description?: ReactNode
  children?: ReactNode
  /** Footer actions area (buttons). */
  footer?: ReactNode
  /** Max content width. Defaults to a comfortable dialog width. */
  size?: 'sm' | 'md' | 'lg'
  /** When false, clicking the scrim does not close the dialog (e.g. destructive confirms). */
  closeOnOverlayClick?: boolean
  /** Hide the default header × button (ConfirmDialog keeps its own actions). */
  hideCloseButton?: boolean
  /** Accessible label when no visible `title` is supplied. */
  ariaLabel?: string
}

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'

/**
 * Accessible modal dialog (KAN-1179).
 *
 * - `role="dialog"` + `aria-modal` + labelled by the title (or `ariaLabel`).
 * - Focus is trapped inside; the previously-focused element is restored on close.
 * - Esc closes; the overlay click closes (opt-out via `closeOnOverlayClick={false}`).
 * - Body scroll is locked while open.
 * - Rendered via a portal to `document.body` so it escapes overflow/stacking contexts.
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  closeOnOverlayClick = true,
  hideCloseButton = false,
  ariaLabel
}: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null)
  const previouslyFocused = useRef<HTMLElement | null>(null)
  const titleId = useId()
  const descId = useId()

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        onClose()
        return
      }
      if (event.key !== 'Tab') return

      const node = dialogRef.current
      if (!node) return
      const focusable = Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        el => el.offsetParent !== null || el === document.activeElement
      )
      if (focusable.length === 0) {
        event.preventDefault()
        node.focus()
        return
      }
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      const active = document.activeElement

      if (event.shiftKey && active === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && active === last) {
        event.preventDefault()
        first.focus()
      }
    },
    [onClose]
  )

  // Lock body scroll + capture/restore focus while open.
  useEffect(() => {
    if (!open) return
    previouslyFocused.current = document.activeElement as HTMLElement | null
    const { overflow } = document.body.style
    document.body.style.overflow = 'hidden'

    // Focus the first focusable element (or the dialog itself) once mounted.
    const raf = requestAnimationFrame(() => {
      const node = dialogRef.current
      if (!node) return
      const focusable = node.querySelector<HTMLElement>(FOCUSABLE)
      ;(focusable ?? node).focus()
    })

    return () => {
      cancelAnimationFrame(raf)
      document.body.style.overflow = overflow
      previouslyFocused.current?.focus?.()
    }
  }, [open])

  if (!open || typeof document === 'undefined') return null

  return createPortal(
    <div
      className="ui-modal-overlay"
      onMouseDown={event => {
        if (closeOnOverlayClick && event.target === event.currentTarget) {
          onClose()
        }
      }}
    >
      <div
        ref={dialogRef}
        className={`ui-modal ui-modal--${size}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-label={!title ? ariaLabel : undefined}
        aria-describedby={description ? descId : undefined}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
      >
        {(title || !hideCloseButton) && (
          <header className="ui-modal__header">
            {title && (
              <h2 id={titleId} className="ui-modal__title">
                {title}
              </h2>
            )}
            {!hideCloseButton && (
              <button
                type="button"
                className="ui-modal__close"
                onClick={onClose}
                aria-label="Close dialog"
              >
                ×
              </button>
            )}
          </header>
        )}
        {description && (
          <p id={descId} className="ui-modal__description">
            {description}
          </p>
        )}
        {children != null && <div className="ui-modal__body">{children}</div>}
        {footer && <footer className="ui-modal__footer">{footer}</footer>}
      </div>
    </div>,
    document.body
  )
}

export default Modal
